import React, { useState } from "react";
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Avatar,
	Collapse,
	CircularProgress,
	Alert
} from "@mui/material";
import {
	KeyboardArrowDown as ExpandIcon,
	KeyboardArrowUp as CollapseIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import i18n from "../../i18n/i18n";
import { useActivityReportsSummary } from "../../hooks/useFreelancerActivityReports";
import { UserType } from "../../models/UserType";
import { zoullooColors } from "../../theme";
import { stringAvatar } from "../../utils/avatarUtils";
import { formatCurrency, formatDate } from "../../utils/formatting";

interface ActivityReportsSummaryTableProps {
	projectId: number;
	userType: UserType;
	isInternalUser: boolean;
}

const getMonthLabel = (month: number, year: number): string => {
	const date = new Date(year, month - 1, 1);
	const locale = i18n.language === "fr" ? fr : enUS;
	const monthName = format(date, "MMMM", { locale });
	return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
};

const ActivityReportsSummaryTable: React.FC<ActivityReportsSummaryTableProps> = ({
	projectId,
	userType,
	isInternalUser
}) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);

	const { data: summary, isLoading, error } = useActivityReportsSummary(projectId);

	const isFreelancer = userType === UserType.Freelancer;
	const isClient = userType === UserType.Client;
	const feePercentage = summary?.feePercentage ?? 0;

	// Financial calculations — same formula as billing worker (Pennylane)
	// ClientPrice = FreelancerPrice / (1 - feePercentage / 100)
	// VA = ClientPrice - FreelancerPrice
	const calcClientPrice = (caTalent: number) => {
		if (feePercentage <= 0 || feePercentage >= 100) return caTalent;
		return caTalent / (1 - feePercentage / 100);
	};
	const calcVA = (caTalent: number) => calcClientPrice(caTalent) - caTalent;
	const calcCA = (caTalent: number) => calcClientPrice(caTalent);
	const calcMontant = (caTalent: number) => calcClientPrice(caTalent);

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" py={4}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ mb: 2 }}>
				{t("project.activityReportsSummary.errorLoading")}
			</Alert>
		);
	}

	const talent = summary?.freelancerSummary;
	if (!talent) return null;

	const pictureUrl = talent.pictureUrlPath
		? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${talent.pictureUrlPath}`
		: undefined;

	return (
		<Paper elevation={1} sx={{ border: `1px solid ${zoullooColors.grey[400]}`, borderRadius: 2, overflow: "hidden" }}>
			{/* Header */}
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				px={3}
				py={2}
				sx={{ cursor: "pointer" }}
				onClick={() => setExpanded(!expanded)}
			>
				<Typography variant="h4" color={zoullooColors.secondary.dark}>
					{t("project.activityReportsSummary.title")}
				</Typography>
				{expanded ? <CollapseIcon /> : <ExpandIcon />}
			</Box>

			<Collapse in={expanded}>
				<TableContainer>
					<Table size="small" sx={{ width: "100%" }}>
						<TableHead>
							<TableRow sx={{ bgcolor: zoullooColors.grey[100] }}>
								<TableCell sx={{ fontWeight: 600 }}>
									{t("project.activityReportsSummary.nameColumn")}
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: 600, px: 1, fontSize: "0.75rem" }}>
									{t("project.activityReportsSummary.contractualDays")}
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: 600, px: 1, fontSize: "0.75rem" }}>
									{t("project.activityReportsSummary.completedDays")}
								</TableCell>
								<TableCell align="center" sx={{ fontWeight: 600, px: 1, fontSize: "0.75rem" }}>
									{t("project.activityReportsSummary.remainingDays")}
								</TableCell>

								{/* Financial columns based on role */}
								{isClient && (
									<TableCell align="center" sx={{ fontWeight: 600, px: 1 }}>
										{t("project.activityReportsSummary.montant")}
									</TableCell>
								)}
								{isFreelancer && (
									<TableCell align="center" sx={{ fontWeight: 600, px: 1 }}>
										{t("project.activityReportsSummary.caTalent")}
									</TableCell>
								)}
								{isInternalUser && (
									<>
										<TableCell align="center" sx={{ fontWeight: 600, px: 1 }}>
											{t("project.activityReportsSummary.caTalent")}
										</TableCell>
										<TableCell align="center" sx={{ fontWeight: 600, px: 1 }}>
											{t("project.activityReportsSummary.caZoulloo")}
										</TableCell>
										<TableCell align="center" sx={{ fontWeight: 600, px: 1 }}>
											{t("project.activityReportsSummary.vaZoulloo")}
										</TableCell>
									</>
								)}

							</TableRow>
						</TableHead>
						<TableBody>
							{/* Main talent row */}
							<TableRow>
								<TableCell>
									<Box display="flex" alignItems="center" gap={1.5}>
										<Avatar
											{...stringAvatar(`${talent.firstName} ${talent.lastName}`)}
											src={pictureUrl}
											sx={{ width: 36, height: 36 }}
										/>
										<Typography variant="body2" fontWeight={500}>
											{talent.firstName} {talent.lastName}
										</Typography>
									</Box>
								</TableCell>
								<TableCell align="center">{talent.contractualDays}</TableCell>
								<TableCell align="center">{talent.completedDays}</TableCell>
								<TableCell align="center">{talent.remainingDays}</TableCell>

								{/* Financial cells — 2 lines: realized / contractual */}
								{isClient && (
									<TableCell align="center">
										<TwoLineAmount
											realized={calcMontant(talent.caTalentRealized)}
											contractual={calcMontant(talent.caTalentContractual)}
										/>
									</TableCell>
								)}
								{isFreelancer && (
									<TableCell align="center">
										<TwoLineAmount
											realized={talent.caTalentRealized}
											contractual={talent.caTalentContractual}
										/>
									</TableCell>
								)}
								{isInternalUser && (
									<>
										<TableCell align="center">
											<TwoLineAmount
												realized={talent.caTalentRealized}
												contractual={talent.caTalentContractual}
											/>
										</TableCell>
										<TableCell align="center">
											<TwoLineAmount
												realized={calcVA(talent.caTalentRealized)}
												contractual={calcVA(talent.caTalentContractual)}
											/>
										</TableCell>
										<TableCell align="center">
											<TwoLineAmount
												realized={calcCA(talent.caTalentRealized)}
												contractual={calcCA(talent.caTalentContractual)}
											/>
										</TableCell>
									</>
								)}

							</TableRow>

							{/* Mission dates */}
							<TableRow>
								<TableCell
									colSpan={isInternalUser ? 8 : 6}
									sx={{ py: 1, bgcolor: zoullooColors.grey[50] }}
								>
									<Typography variant="body2" color="text.secondary">
										{t("project.activityReportsSummary.missionDates", {
											start: formatDate(summary?.projectStartDate),
											end: formatDate(summary?.projectEndDate)
										})}
									</Typography>
								</TableCell>
							</TableRow>

							{/* Monthly detail rows */}
							{talent.monthlyDetails.map(detail => (
								<TableRow key={detail.reportId}>
									<TableCell sx={{ py: 1 }}>
										<Typography variant="caption" sx={{ display: "block", lineHeight: 1.4 }}>
											{getMonthLabel(detail.month, detail.year)}
											{" • "}
											<Typography component="span" variant="caption" color="text.secondary">
												{formatDate(detail.startDate)} - {formatDate(detail.endDate)}
											</Typography>
											{" • "}
											{detail.workedUnits ?? 0} {t("project.activityReportsSummary.unitLabel")}
										</Typography>
									</TableCell>
									<TableCell />
									<TableCell align="center">
										<Typography variant="body2" fontWeight={detail.workedUnits ? 600 : 400}>
											{detail.workedUnits ?? "-"}
										</Typography>
									</TableCell>
									<TableCell />

									{/* Monthly financial amounts */}
									{isClient && (
										<TableCell align="center">
											<Typography variant="body2">
												{formatCurrency(detail.totalReported ? calcMontant(detail.totalReported) : undefined)}
											</Typography>
										</TableCell>
									)}
									{isFreelancer && (
										<TableCell align="center">
											<Typography variant="body2">
												{formatCurrency(detail.totalReported ?? undefined)}
											</Typography>
										</TableCell>
									)}
									{isInternalUser && (
										<>
											<TableCell align="center">
												<Typography variant="body2">
													{formatCurrency(detail.totalReported ?? undefined)}
												</Typography>
											</TableCell>
											<TableCell align="center">
												<Typography variant="body2">
													{formatCurrency(detail.totalReported ? calcVA(detail.totalReported) : undefined)}
												</Typography>
											</TableCell>
											<TableCell align="center">
												<Typography variant="body2">
													{formatCurrency(detail.totalReported ? calcCA(detail.totalReported) : undefined)}
												</Typography>
											</TableCell>
										</>
									)}

								</TableRow>
							))}

							{/* Summary row */}
							<TableRow>
								<TableCell
									colSpan={isInternalUser ? 8 : 6}
									sx={{ py: 2 }}
								>
									<Typography variant="body2" fontWeight={600}>
										{isFreelancer
											? t("project.activityReportsSummary.talentSummary", {
													completed: talent.completedDays,
													total: talent.contractualDays
												})
											: t("project.activityReportsSummary.projectSummary", {
													completed: talent.completedDays,
													total: talent.contractualDays
												})}
									</Typography>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</Collapse>
		</Paper>
	);
};

/** Two-line amount display: realized on top, contractual below (lighter) */
const TwoLineAmount: React.FC<{ realized: number; contractual: number }> = ({
	realized,
	contractual
}) => (
	<Box>
		<Typography variant="body2">{formatCurrency(realized)}</Typography>
		<Typography variant="caption" color="text.secondary">
			{formatCurrency(contractual)}
		</Typography>
	</Box>
);

export default ActivityReportsSummaryTable;
